/**
 * CreditByDom — "App designed and managed by Dom" credit + profile card.
 *
 * (User ask 2026-05-25: "where can we add 'app designed and managed by
 * Dom' and make my name clickable to display me like a report card" →
 * follow-up: "show my profile picture and maybe show message dom or open
 * a ticket maybe show my highlights".)
 *
 * Renders a small underlined credit line on the HomePage footer. Tapping
 * "Dom" opens a friendly profile-card modal with:
 *   - Avatar photo (big, with a soft gradient ring)
 *   - Name + the user's actual title (Admin / SM / DM / Wow Crew)
 *   - 3 highlight stats (total emails captured, best day, current streak)
 *     pulled from the existing /api/crew-report endpoint
 *   - Two primary CTAs:
 *       • "Message Dom" — dispatches `wowapp:open-dm` with his crew_id,
 *         HomePage listens and routes into Inbox with the thread open.
 *       • "Open a Ticket" — dispatches `wowapp:open-ticket`, HomePage
 *         flips on the ReportIssueModal it already manages.
 *
 * The founder id + highlights are fetched lazily on first open and
 * cached at the module level so the second tap is instant.
 */
import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { ChatTeardropDots, Bug, X } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so repeat opens within the same session skip the fetch.
let _cachedFounder = null;   // { id, first_name, photo_url }
let _cachedReport = null;    // /api/crew-report response

export default function CreditByDom({ variant = "dark" }) {
  const [open, setOpen] = useState(false);
  const [founder, setFounder] = useState(_cachedFounder);
  const [report, setReport] = useState(_cachedReport);

  // Lazy-load founder + their report on first open.
  useEffect(() => {
    if (!open) return;
    let aborted = false;
    (async () => {
      try {
        if (!_cachedFounder) {
          const { data } = await axios.get(`${API}/credits/founder`);
          if (aborted) return;
          if (data?.id) {
            _cachedFounder = data;
            setFounder(data);
          }
        }
        const f = _cachedFounder;
        if (f?.id && !_cachedReport) {
          const { data: rpt } = await axios.get(`${API}/crew-report/${f.id}`);
          if (aborted) return;
          _cachedReport = rpt;
          setReport(rpt);
        }
      } catch (e) {
        // Soft-fail — credit line shouldn't disrupt anything.
        console.error("[credits] founder fetch failed", e);
      }
    })();
    return () => { aborted = true; };
  }, [open]);

  const handleMessage = useCallback(() => {
    if (!founder?.id) return;
    window.dispatchEvent(new CustomEvent("wowapp:open-dm", { detail: { crewId: founder.id } }));
    setOpen(false);
  }, [founder]);

  const handleTicket = useCallback(() => {
    window.dispatchEvent(new CustomEvent("wowapp:open-ticket"));
    setOpen(false);
  }, []);

  const baseStyles = variant === "light" ? "text-white/55" : "text-slate-400";
  const linkStyles = variant === "light"
    ? "text-white/85 underline decoration-white/30 hover:text-white hover:decoration-white"
    : "underline decoration-slate-300 hover:decoration-blue-500";

  // Role badge — derive from the founder doc (which carries the live
  // role flags) so it stays in sync if Dom ever changes role. Admin wins
  // because that's his actual privilege; the "Founder" suffix anchors
  // the credit line context.
  const roleLabel = (() => {
    if (!founder) return "";
    if (founder.is_admin) return "Admin";
    if (founder.is_district_manager) return "District Manager";
    if (founder.position) return founder.position;
    if (founder.is_manager) return "Manager";
    return "Wow Crew";
  })();

  return (
    <>
      <p
        className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${baseStyles}`}
        data-testid="credit-by-dom"
      >
        App designed by{" "}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`font-black transition-colors ${linkStyles}`}
          style={variant === "dark" ? { color: "var(--fb-blue)" } : undefined}
          data-testid="credit-by-dom-link"
          aria-label="Open Dom's profile card"
        >
          Dom
        </button>
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-sm p-0 gap-0 overflow-hidden bg-white border-0 shadow-2xl [&>button]:hidden"
          data-testid="founder-profile-card"
        >
          {/* Single relative wrapper so the avatar can absolute-overlap
              the gradient. (Earlier attempt used sibling divs but
              shadcn DialogContent's `gap-4` jammed them apart.) Also
              [&>button]:hidden on the parent kills shadcn's default
              close X — we render our own circular one over the
              gradient. (User feedback 2026-05-25 "why are there two
              x's? come on fix this already".) */}
          <div className="relative">
            {/* Gradient band */}
            <div
              className="h-28 w-full"
              style={{
                background:
                  "linear-gradient(135deg, var(--fb-blue, #0048B3) 0%, #3B82F6 60%, #60A5FA 100%)",
              }}
            >
              {/* Subtle noise so the gradient doesn't read flat. */}
              <div
                className="absolute inset-x-0 top-0 h-28 opacity-20 pointer-events-none mix-blend-overlay"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.4) 0px, transparent 40%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.25) 0px, transparent 30%)",
                }}
              />
            </div>

            {/* Single close button — circular over the gradient. */}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors z-10"
              aria-label="Close"
              data-testid="founder-profile-close"
            >
              <X weight="bold" className="w-3.5 h-3.5" />
            </button>

            {/* Avatar — absolute-positioned so it ACTUALLY overlaps the
                gradient. Sits half over, half below the band edge. */}
            <div className="absolute left-1/2 -translate-x-1/2 top-16 z-[5]">
              {founder?.photo_url ? (
                <img
                  src={founder.photo_url}
                  alt={founder?.first_name || "Dom"}
                  className="w-24 h-24 rounded-full object-cover object-top ring-4 ring-white shadow-lg bg-white"
                  data-testid="founder-profile-photo"
                />
              ) : (
                <div
                  className="w-24 h-24 rounded-full ring-4 ring-white shadow-lg flex items-center justify-center text-white font-black"
                  style={{ backgroundColor: "var(--fb-blue)", fontFamily: "'Five Below Gothic', Chivo, sans-serif", fontSize: 36 }}
                >
                  {(founder?.first_name || "D").charAt(0)}
                </div>
              )}
            </div>
          </div>

          {/* Body — pt-14 reserves room for the half of the avatar that
              hangs below the gradient band. */}
          <div className="px-6 pt-14 pb-5">
            <div className="flex flex-col items-center">
              <h2
                className="text-2xl font-black text-slate-900 leading-tight"
                style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.01em" }}
                data-testid="founder-profile-name"
              >
                {founder?.first_name || "Dom"}
              </h2>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mt-0.5" data-testid="founder-profile-role">
                {roleLabel || "App Designer · Founder"}
              </p>
              {/* Store assignment — "Store Manager · #3094 Wheat Ridge,
                  CO". Pulled from the founder doc so it stays accurate
                  if Dom ever transfers. */}
              {(founder?.position || founder?.home_store_code) && (
                <p className="text-[11px] font-bold text-slate-700 mt-1.5" data-testid="founder-profile-store">
                  {founder?.position && <span>{founder.position}</span>}
                  {founder?.position && founder?.home_store_code && <span className="text-slate-400 mx-1">·</span>}
                  {founder?.home_store_code && (
                    <span>
                      #{founder.home_store_code}
                      {founder?.home_store_city ? ` ${founder.home_store_city}` : ""}
                    </span>
                  )}
                </p>
              )}
            </div>

            {/* Highlights — three big-number tiles. */}
            <div className="grid grid-cols-3 gap-2 mt-5" data-testid="founder-profile-highlights">
              <HighlightTile label="Captures" value={report?.total_emails ?? "—"} accent="blue" />
              <HighlightTile label="Best Day" value={report?.best_count ?? "—"} accent="amber" />
              <HighlightTile label="Day Streak" value={report?.streak ?? "—"} accent="rose" suffix={report?.streak ? "🔥" : ""} />
            </div>

            {/* Mission tagline. */}
            <p className="text-center text-xs text-slate-500 mt-4 leading-relaxed">
              Built the WoW App so <span className="font-bold text-slate-700">Colorado West</span> can be the best in the Mountain Region. Hit me up — feedback &amp; ideas are how it gets better.
            </p>

            {/* CTAs. */}
            <div className="flex flex-col gap-2 mt-5">
              <Button
                className="fb-btn-primary w-full rounded-full"
                onClick={handleMessage}
                disabled={!founder?.id}
                data-testid="founder-profile-message"
              >
                <ChatTeardropDots weight="fill" className="w-4 h-4 mr-1.5" />
                Message Dom
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-full border-slate-200 text-slate-700 hover:bg-slate-50"
                onClick={handleTicket}
                data-testid="founder-profile-ticket"
              >
                <Bug weight="bold" className="w-4 h-4 mr-1.5" />
                Open a Ticket
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function HighlightTile({ label, value, accent = "blue", suffix = "" }) {
  const palette = {
    blue:  { bg: "#DBEAFE", text: "#0048B3" },
    amber: { bg: "#FEF3C7", text: "#B45309" },
    rose:  { bg: "#FFE4E6", text: "#BE123C" },
  }[accent] || { bg: "#F1F5F9", text: "#0F172A" };
  return (
    <div className="rounded-xl px-2 py-3 text-center" style={{ backgroundColor: palette.bg }}>
      <p
        className="text-2xl font-black leading-none"
        style={{ color: palette.text, fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
      >
        {value}{suffix}
      </p>
      <p className="text-[10px] uppercase tracking-[0.14em] font-bold mt-1" style={{ color: palette.text, opacity: 0.85 }}>
        {label}
      </p>
    </div>
  );
}
